import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { NbDialogService } from '@nebular/theme'
import { Observable, of, Subscription, throwError, timer } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'
import { environment } from '../../../../environments/environment'
import { FFService } from '../../ff.service'
import { SessionTimeoutService } from '../session_timeout/session_timeout.service'
import { User } from './user'

@Injectable( {
  providedIn: 'root',
} )
export class AuthenticationService {
  endpoint = environment.api_endpoint;
  headers = new HttpHeaders().set( 'Content-Type', 'application/json' );
  current_user_record = {};
  current_session = {};
  ff
  is_mfa_complete = false;

  private polling_subscription: Subscription
  private UNAUTHENTICATED_POLLING_INTERVAL = 5000;
  private AUTHENTICATED_POLLING_INTERVAL = 30000;

  is_validating = false;

  // Your other properties remain unchanged
  authentication_validation_check_interval
  inactive_dialog_timeout
  dialog_visible = false;
  dialog

  constructor(
    private http: HttpClient,
    public router: Router,
    private ff_service: FFService,
    private session_service: SessionTimeoutService,
    private dialogService: NbDialogService,
  ) {
    this.ff = this.ff_service.ff
  }

  // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>> START OF REVISED METHODS <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  /**
   * Starts a dynamic polling loop to check authentication state.
   * The polling interval changes based on whether the user is fully authenticated.
   * This is the central logic for session management.
   */
  start_validation_interval () {
    this.end_validation_interval() // Stop any existing loop first
    this.is_validating = true

    const polling_duration = this.is_logged_in ? this.AUTHENTICATED_POLLING_INTERVAL : this.UNAUTHENTICATED_POLLING_INTERVAL

    this.polling_subscription = timer( 0, polling_duration ).subscribe( () => {
      // Check for user activity and reset session on the server if needed
      if ( this.session_service.is_user_active ) {
        this.session_service.is_user_active = false // Reset the flag
        this.session_service.reset_session_timer().subscribe( {
          complete: () => {
            // On completion of reset_session_timer, immediately validate the auth state
            this.validate_and_update_state()
          }
        } )
      } else {
        // If the user is not active, just validate the auth state without a keep-alive
        this.validate_and_update_state()
      }
    } )
  }

  /**
   * A helper method to perform a validation check and update the app state.
   */
  private validate_and_update_state () {
    this.validate_authentication().subscribe( is_authenticated => {
      // If the authentication state changes, restart the interval with the new duration.
      if ( this.is_logged_in !== is_authenticated ) {
        this.start_validation_interval()
      }
      if ( !is_authenticated ) {
        this.internal_logout()
      }
    } )
  }

  /**
   * Stops the polling interval.
   */
  end_validation_interval () {
    if ( this.polling_subscription ) {
      this.polling_subscription.unsubscribe()
      this.is_validating = false
    }
  }

  /**
   * Posts to the authentication state endpoint and returns a boolean
   * indicating if the user is fully authenticated.
   * This method is now defensive against malformed responses.
   */
  validate_authentication (): Observable<boolean> {
    const token = this.get_token()
    if ( !token ) {
      return of( false ) // No token, no auth
    }

    return this.http.post( `${this.endpoint}/service/session/get_authentication_state`, {}, {
      headers: {
        'Content-Type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
    } ).pipe(
      map( ( response: any ) => {
        // Defensive check: ensure the response is an array and has at least one object
        const auth_state = ( Array.isArray( response ) && response.length > 0 ) ? response[0] : null

        if ( auth_state && auth_state.sunset_timestamp_epoch_ms ) {
          // Pass the sunset timestamp to the SessionTimeoutService
          this.session_service.sunset_timestamp_epoch_ms = auth_state.sunset_timestamp_epoch_ms
        }

        // Defensive check: return true only if is_fully_authenticated exists and is true
        return !!auth_state && !!auth_state.is_fully_authenticated
      } ),
      catchError( err => {
        // If the request itself fails, it's an unauthenticated state
        console.error( 'Validation check failed:', err )
        return of( false )
      } ),
    )
  }

  /**
   * Sign-in method, modified to start the polling interval on success.
   */
  login ( user: User ) {
    return this.http.post<any>( `${this.endpoint}/service/authentication/authenticate`, user ).pipe(
      tap( response => {
        if ( response.token ) {
          sessionStorage.setItem( 'access_token', response.token )
          this.start_validation_interval() // Start the polling loop after a successful login
        }
      } ),
      catchError( this.handle_error ),
    )
  }

  /**
   * The internal logout method, modified to stop the polling loop.
   */
  internal_logout () {
    sessionStorage.removeItem( 'access_token' )
    sessionStorage.removeItem( 'current_user_data' )
    this.end_validation_interval() // Stop the polling loop
    this.router.navigate( ['/application/authentication'] )
  }

  get is_logged_in (): boolean {
    const authToken = sessionStorage.getItem( 'access_token' )
    return ( authToken !== null )
  }
  // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>> END OF REVISED METHODS <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<


  // The rest of your existing methods are unchanged and can be kept.
  signUp ( user: User ): Observable<any> {
    const api = `${this.endpoint}/service/user_manager/register_user`
    return this.http.post( api, user )
      .pipe(
        catchError( this.handle_error ),
      )
  }

  // Gets a list of users
  get_users () {
    const query = [
      {
        $match: { mime_type: "access_control/account" }
      },
      {
        $lookup: {
          from: "object",
          let: { email: "$user_contact_email" },
          pipeline: [
            {
              $match: {
                $expr: {
                  $and: [
                    { $eq: ["$mime_type", "audit/event"] },
                    { $eq: ["$event_code", "user_login"] },
                    { $eq: ["$user_contact_email", "$$email"] }
                  ]
                }
              }
            },
            { $sort: { created_timestamp_epoch_ms: -1 } },
            { $limit: 1 }
          ],
          as: "last_login_audit_record"
        }
      },
      {
        $addFields: {
          last_login_audit_record: {
            $cond: {
              if: { $gt: [{ $size: "$last_login_audit_record" }, 0] },
              then: { $arrayElemAt: ["$last_login_audit_record", 0] },
              else: "$$REMOVE"
            }
          }
        }
      },
      {
        $sort: { first_name: 1 }
      }
    ]
    return this.http.post<any>( `${this.endpoint}/service/storage/query`, query )
  }

  // Used to create or update a user on the back-end
  store ( user ) {
    return this.http.post<any>( `${this.endpoint}/service/storage/store`, user )
  }

  // Update / create user record
  upsert_user ( user ) {
    const action = [{
      mime_type: 'access_control/account',
      uri: environment.production ? this.current_user.uri : 'test_uri_for_password_change',
      old_password: user.current_password,
      new_password: user.new_password_1,
    }]
    return this.http.post<any>( `${this.endpoint}/service/storage/store`, action )
  }

  get_token () {
    return sessionStorage.getItem( 'access_token' )
  }

  get current_user (): any {
    return this.current_user_record
  }

  get session (): any {
    return this.current_session
  }

  get_current_user (): any {
    return this.http.get<any>( `${this.endpoint}/service/session/get_session`,
      { observe: 'response' } )
      .pipe(
        map( ( response: any ) => {
          if ( response.ok ) {
            this.current_session = response.body[0]
            if ( response.body[0].hasOwnProperty( 'user_record' ) ) {
              this.current_user_record = response.body[0].user_record[0]
            } else {
              console.error( 'User record retrieval failed. Please contact support.' )
              this.logout()
            }
            this.ff.ff_service.publish( response.body[0] ).to( '/local/service/session' )
          }
          return response.ok
        } ),
        catchError( error => {
          console.error( error )
          return of( error )
        } ),
      )
  }

  logout () {
    this.http.post<any>( `${this.endpoint}/service/session/logout`, {} ).subscribe( () => {
      this.internal_logout()
    } )
  }

  // User profile
  get_user_profile ( id ): Observable<any> {
    const api = `${this.endpoint}/service/user_manager/get_user_profile/${id}`
    return this.http.get( api, { headers: this.headers } ).pipe(
      map( ( res: Response ) => {
        return res || {}
      } ),
      catchError( this.handle_error ),
    )
  }

  // Error
  handle_error ( error: HttpErrorResponse ) {
    let msg = ''
    if ( error.error instanceof ErrorEvent ) {
      msg = error.error.message
    } else {
      msg = `Error Code: ${error.status}\nMessage: ${error.message}`
    }
    return throwError( msg )
  }

  // Get a list of password reset links that are valid
  reset_password_link_get_if_enabled$ ( id: string ) {
    if ( !id ) return undefined

    return this.http.post<any>( `${this.endpoint}/service/storage/query`, [{
      $match: {
        uri: id,
        mime_type: 'access_control/account',
        is_enabled: true,
      },
    }] )
  }
}
